import { useFormState } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { AlertError } from 'Common/AlertError';
import {
  CheckboxField,
  EmailField,
  PasswordAndConfirmPasswordFields,
  TextField
} from 'Common/Form';
import { RegistrationSchema } from 'Common/Form/ValidationSchemas';
import { AcceptTermsAndPrivacyPolicyMessage } from 'Components';
import { StyledAuthFormContainer, StyledButton } from 'Components/Styled';
import { useAppForm } from 'Utils/customHooks';

import type { IRegisterRequest } from '@libs/models';

const formFieldNames = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  password: 'password',
  confirmPassword: 'confirmPassword',
  acceptTermsAndPrivacyPolicy: 'acceptTermsAndPrivacyPolicy'
} as const;

type Props = {
  onSubmit: (formData: IRegisterRequest) => void;
  error?: string | null;
  isSubmitSuccess?: boolean;
};

export const RegistrationForm = ({ onSubmit, error, isSubmitSuccess }: Readonly<Props>) => {
  const { t } = useTranslation();
  const formContext = useAppForm<IRegisterRequest>(RegistrationSchema());
  const { isValid } = useFormState({ control: formContext.control });

  return !isSubmitSuccess ? (
    <FormContainer onSuccess={onSubmit} formContext={formContext}>
      <StyledAuthFormContainer>
        {error && <AlertError text={t(error)} />}

        <TextField name={formFieldNames.firstName} label={t('auth.firstName')} required />

        <TextField name={formFieldNames.lastName} label={t('auth.lastName')} />

        <EmailField name={formFieldNames.email} />

        <PasswordAndConfirmPasswordFields formContext={formContext} />

        <CheckboxField
          name={formFieldNames.acceptTermsAndPrivacyPolicy}
          label={<AcceptTermsAndPrivacyPolicyMessage />}
        />

        <StyledButton type="submit" size="large" disabled={!isValid}>
          {t('auth.createAccount')}
        </StyledButton>
      </StyledAuthFormContainer>
    </FormContainer>
  ) : (
    <div>
      {t('auth.registrationMessagePart1')} {formContext.control._formValues.email}
      {t('auth.registrationMessagePart2')}
    </div>
  );
};
